import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Form, InputGroup, ProgressBar, Badge, Nav, Tab, Card, Spinner } from 'react-bootstrap';
import { FaCloudUploadAlt, FaLink, FaTrash, FaCheckCircle, FaCamera, FaSync } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Webcam from 'react-webcam';
import mediaService from "../../../../services/mediaService";

const ImageUploader = ({ value, onChange, label = "Image" }) => {
    const [activeTab, setActiveTab] = useState('upload');
    const [preview, setPreview] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Camera & Device Management
    const [isCameraStarted, setIsCameraStarted] = useState(false);
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState('');

    // --- REFS (Including the missing fileInputRef) ---
    const webcamRef = useRef(null);
    const fileInputRef = useRef(null); // Fixes ESLint no-undef

    // 1. Sync preview with incoming value
    useEffect(() => {
        if (value?.imageUrl) {
            setPreview(value.imageUrl);
            setActiveTab(value.imagePublicId ? 'upload' : 'url');
        } else {
            setPreview('');
        }
    }, [value]);

    // 2. Camera hardware logic and enumeration
    const refreshDevices = useCallback(async () => {
        try {
            const mediaDevices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = mediaDevices.filter(({ kind }) => kind === "videoinput");
            setDevices(videoDevices);
            if (videoDevices.length > 0 && !selectedDeviceId) {
                setSelectedDeviceId(videoDevices[0].deviceId);
            }
        } catch (err) {
            toast.error("Error listing cameras");
        }
    }, [selectedDeviceId]);

    useEffect(() => {
        if (activeTab === 'camera') {
            refreshDevices();
        } else {
            setIsCameraStarted(false);
        }
    }, [activeTab, refreshDevices]);

    // Cleanup hardware stream on unmount or tab change
    useEffect(() => {
        return () => {
            if (webcamRef.current && webcamRef.current.video) {
                const stream = webcamRef.current.video.srcObject;
                if (stream) stream.getTracks().forEach(track => track.stop());
            }
        };
    }, [activeTab]);

    const clearState = () => {
        setPreview('');
        setSelectedFile(null);
        setUploadProgress(0);
        setIsCameraStarted(false);
        onChange({ imageUrl: null, imagePublicId: null });
    };

    const handleFileSelect = async (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setSelectedFile(file);
            setPreview(URL.createObjectURL(file));
            await handleUpload(file);
        }
    };

    const capturePhoto = useCallback(() => {
        const imageSrc = webcamRef.current?.getScreenshot();
        if (imageSrc) {
            fetch(imageSrc)
                .then(res => res.blob())
                .then(async blob => {
                    const file = new File([blob], `snap-${Date.now()}.jpg`, { type: 'image/jpeg' });
                    setSelectedFile(file);
                    setPreview(imageSrc);
                    setIsCameraStarted(false); // Resource discipline
                    await handleUpload(file);
                });
        }
    }, [webcamRef]);

    const handleUpload = async (fileToUpload = selectedFile) => {
        if (!fileToUpload) return;
        try {
            setIsUploading(true);
            const data = await mediaService.uploadToCloudinary(fileToUpload, setUploadProgress);
            onChange({ imageUrl: data.secure_url, imagePublicId: data.public_id });
            toast.success("Cloud Sync Complete");
            setSelectedFile(null);
        } catch (error) {
            toast.error("Cloud Upload Failed");
        } finally {
            setIsUploading(false);
        }
    };

    // --- Drag & Drop Handlers (Restored) ---
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            if (file.type.startsWith('image/')) {
                setSelectedFile(file);
                setPreview(URL.createObjectURL(file));
                setActiveTab('upload'); // Ensure we are on the upload tab
                await handleUpload(file);
            } else {
                toast.error("Please drop an image file.");
            }
        }
    };
    // ----------------------------------------

    // --- SHARED PREVIEW COMPONENT (Expert UX consistency) ---
    const RenderPreview = () => (
        <div className="w-100 mt-2 animate__animated animate__fadeIn">
            <div className="preview-container border rounded bg-light overflow-hidden position-relative"
                style={{ height: '240px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img
                    src={preview}
                    alt="Preview"
                    className="h-100 w-100 shadow-inner"
                    style={{ objectFit: 'contain', backgroundColor: '#f8f9fa' }}
                />
                {value?.imagePublicId && (
                    <Badge bg="success" className="position-absolute top-0 end-0 m-2 shadow-sm border border-white border-2">
                        <FaCheckCircle className="me-1" /> CLOUD MASTER
                    </Badge>
                )}
            </div>
            <div className="mt-2 d-flex gap-2">
                {isUploading ? (
                    <div className="w-100 text-center py-2 text-primary fw-bold small">
                        <Spinner animation="border" size="sm" className="me-2" /> UPLOADING TO CLOUD {uploadProgress}%
                    </div>
                ) : (
                    <Button variant="outline-danger" size="sm" className="w-100 fw-bold border-dashed mt-2" onClick={clearState}>
                        <FaTrash className="me-2" /> REMOVE IMAGE
                    </Button>
                )}
            </div>
            {isUploading && <ProgressBar animated now={uploadProgress} variant="primary" className="mt-2 shadow-sm" style={{ height: '6px' }} />}
        </div>
    );

    return (
        <div className="image-uploader-wrapper">
            <style>{`
                .upload-zone { border: 2px dashed #d9dee3; border-radius: 0.75rem; padding: 2.5rem; cursor: pointer; transition: 0.3s; background: #f8f9fa; }
                .upload-zone:hover { background: #f0f1f4; border-color: #696cff; transform: translateY(-2px); }
                .custom-nav-tabs .nav-link { border: none; color: #8592a3; font-weight: 700; padding: 0.85rem; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05rem; }
                .custom-nav-tabs .nav-link.active { color: #696cff; background: #fff; border-bottom: 3px solid #696cff; }
            `}</style>

            <Form.Label className="premium-label mb-2 d-block">{label}</Form.Label>

            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                <Card className="border shadow-sm overflow-hidden" style={{ borderRadius: '1rem' }}>
                    <Card.Header className="p-0 bg-light border-bottom-0">
                        <Nav style={{ zIndex: 1000 }} variant="tabs" className="nav-justified custom-nav-tabs">
                            <Nav.Item><Nav.Link eventKey="upload"><FaCloudUploadAlt className="me-2" /> File</Nav.Link></Nav.Item>
                            <Nav.Item><Nav.Link eventKey="camera"><FaCamera className="me-2" /> Snap</Nav.Link></Nav.Item>
                            <Nav.Item><Nav.Link eventKey="url"><FaLink className="me-2" /> URL</Nav.Link></Nav.Item>
                        </Nav>
                    </Card.Header>

                    <Card.Body className="p-3 bg-white mt-4">
                        <Tab.Content>
                            <Tab.Pane eventKey="upload">
                                {preview ? <RenderPreview /> : (
                                    <div
                                        className="upload-zone text-center"
                                        onClick={() => fileInputRef.current.click()}
                                        onDragOver={handleDragOver}
                                        onDrop={handleDrop}
                                    >
                                        <FaCloudUploadAlt size={48} className="text-primary opacity-50 mb-3" />
                                        <p className="small mb-0 fw-bold text-dark">Click to browse or drop master image</p>
                                        <input ref={fileInputRef} type="file" className="d-none" accept="image/*" onChange={handleFileSelect} />
                                    </div>
                                )}
                            </Tab.Pane>

                            <Tab.Pane eventKey="camera">
                                {preview && !isCameraStarted ? <RenderPreview /> : (
                                    <div className="camera-ui">
                                        {!isCameraStarted ? (
                                            <div className="p-4 border rounded-3 bg-light text-center">
                                                <Form.Select
                                                    size="sm"
                                                    className="mb-3 mx-auto w-100 border-2 shadow-sm"
                                                    value={selectedDeviceId}
                                                    onChange={(e) => setSelectedDeviceId(e.target.value)}
                                                >
                                                    {devices.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Detecting Camera...'}</option>)}
                                                </Form.Select>
                                                <Button variant="primary" className="fw-bold px-5 shadow" onClick={() => setIsCameraStarted(true)}>
                                                    <FaCamera className="me-2" /> ACTIVATE HARDWARE
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="webcam-box bg-dark rounded-3 overflow-hidden position-relative shadow-lg" style={{ height: '240px' }}>
                                                <Webcam
                                                    key={selectedDeviceId}
                                                    audio={false}
                                                    ref={webcamRef}
                                                    screenshotFormat="image/jpeg"
                                                    videoConstraints={{ deviceId: selectedDeviceId, height: 720, width: 1280 }}
                                                    className="h-100 w-100 object-fit-cover"
                                                />
                                                <div className="position-absolute bottom-0 start-0 w-100 p-3 d-flex gap-2 bg-dark bg-opacity-75">
                                                    <Button variant="success" size="sm" onClick={capturePhoto} className="flex-fill fw-bold border-2">CAPTURE RECORD</Button>
                                                    <Button variant="danger" size="sm" className="px-3" onClick={() => setIsCameraStarted(false)}><FaSync /></Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Tab.Pane>

                            <Tab.Pane eventKey="url">
                                {preview ? <RenderPreview /> : (
                                    <div className="p-2 animate__animated animate__fadeIn">
                                        <InputGroup size="sm" className="border rounded-pill overflow-hidden shadow-sm">
                                            <InputGroup.Text className="bg-white border-0 ps-3"><FaLink className="text-muted" /></InputGroup.Text>
                                            <Form.Control
                                                className="border-0 p-2 shadow-none"
                                                placeholder="Paste secure image link (HTTPS)..."
                                                onChange={(e) => {
                                                    setPreview(e.target.value);
                                                    onChange({ imageUrl: e.target.value, imagePublicId: null });
                                                }}
                                            />
                                        </InputGroup>
                                    </div>
                                )}
                            </Tab.Pane>
                        </Tab.Content>
                    </Card.Body>
                </Card>
            </Tab.Container>
        </div>
    );
};

export default ImageUploader;